<template>
  <div>
    <div v-if="lastTicketResult && formattedLayouts.length">
      <div class="flex-wrapper">
        <div v-for="(layout, layoutIndex) in formattedLayouts" :key="layoutIndex" class="grid-container">
          <div class="layout-label">
            Layout {{ layoutIndex + 1 }}
            <span v-if="layout.cascadeEvent > 0" class="cascade-info">
              ({{ getCascadeEventName(layout.cascadeEvent) }})
            </span>
            <span v-if="layout.winAmount > 0" class="win-info">
              Win: {{ layout.winAmount }}
            </span>
            <span v-if="layout.bonusAmount > 0" class="bonus-info">
              Bonus: {{ layout.bonusAmount }}
            </span>
          </div>
          <div class="grid" :style="gridStyle">
            <div v-for="cell in layout.grid" 
                :key="`${cell.x}-${cell.y}`" 
                class="grid-cell"
                :style="getCellStyle(cell)">
              {{ cell.symbol }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="rawLayoutDisplay" class="layout-fallback">
      <div class="layout-label">Raw layout data (not visualizable)</div>
      <pre class="layout-raw-data">{{ rawLayoutDisplay }}</pre>
    </div>
  </div>
</template>

<script>
import { parseInstantLayout } from '../../utils/instantLayoutParser';

const SYMBOL_EMOJI_MAP = {
  A: '🍎',
  B: '🍌',
  C: '🍒',
  D: '🍇',
  E: '🍉',
  F: '🍓',
  G: '🍍',
  H: '🥝',
  I: '🍋',
  MINI: '⭐',
  MEGA: '🏆',
  TNT: '💣',
  W: '🃏'
};

const CASCADE_EVENT_NAMES = {
  0: 'None',
  1: 'Win',
  2: 'Bonus',
  3: 'Bonus & Win',
  4: 'Bomb',
  5: 'Bonus & Bomb'
};

export default {
  name: 'InstantLayoutGenerator',
  props: {
    lastTicketResult: {
      type: Object,
      required: true,
    },
  },
  computed: {
    parsedLayout() {
      const parsed = parseInstantLayout(this.lastTicketResult?.layout);
      return parsed;
    },
    gridStyle() {
      if (!this.parsedLayout || !this.parsedLayout.layoutWidth || !this.parsedLayout.layoutHeight) return {};
      return {
        'grid-template-columns': `repeat(${this.parsedLayout.layoutWidth}, 80px)`,
        'grid-template-rows': `repeat(${this.parsedLayout.layoutHeight}, 80px)`
      };
    },
    formattedLayouts() {
      if (!this.parsedLayout) return [];
      if (!Array.isArray(this.parsedLayout.reveals)) return [];

      const width = this.parsedLayout.layoutWidth;
      const height = this.parsedLayout.layoutHeight;

      return this.parsedLayout.reveals.map(reveal => {
        const grid = [];
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            grid.push({ x, y, symbol: '' });
          }
        }

        reveal.layoutCoordinates.forEach(coord => {
          const index = coord.y * width + coord.x;
          if (index < 0 || index >= grid.length) {
            return;
          }

          grid[index].symbol = SYMBOL_EMOJI_MAP[coord.symbol] || coord.symbol;
        });

        return {
          grid,
          cascadeEvent: reveal.cascadeEvent,
          winAmount: reveal.winAmount,
          bonusAmount: reveal.bonusAmount
        };
      });
    },
    rawLayoutDisplay() {
      const rawLayout = this.lastTicketResult?.layout;
      if (!rawLayout) return '';

      if (typeof rawLayout === 'string') {
        return rawLayout;
      }

      try {
        return JSON.stringify(rawLayout, null, 2);
      } catch {
        return String(rawLayout);
      }
    }
  },
  methods: {
    getCascadeEventName(event) {
      return CASCADE_EVENT_NAMES[event] || 'Unknown';
    },
    getCellStyle(cell) {
      return {
        'grid-row': cell.y + 1,
        'grid-column': cell.x + 1
      };
    }
  }
};
</script>

<style>
.flex-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 40px;
}

.grid-container {
  margin-bottom: 20px;
}

.layout-label {
  font-weight: bold;
  margin-bottom: 10px;
}

.grid {
  display: grid;
  gap: 2px;
}

.grid-cell {
  width: 80px;
  height: 80px;
  display: flex;
  font-size: 75px;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
}

.cascade-info {
  font-size: 0.8em;
  color: #666;
  margin-left: 8px;
}

.win-info {
  color: green;
  font-size: 0.9em;
  margin-top: 4px;
}

.bonus-info {
  color: blue;
  font-size: 0.9em;
  margin-top: 4px;
}

.layout-fallback {
  margin-top: 10px;
}

.layout-raw-data {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
}
</style>